import { useState, useEffect } from 'react';
import { Plus, Trash2, Edit2, Loader2, ArrowRight, Sparkles } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api';

export default function Funds() {
  const [funds, setFunds] = useState([]);
  const [incomes, setIncomes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isScanning, setIsScanning] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestionSort, setSuggestionSort] = useState('amount_desc');
  
  const [isCreating, setIsCreating] = useState(false);
  const [newFundName, setNewFundName] = useState('');
  const [newFundType, setNewFundType] = useState('expense');
  const [newFundTarget, setNewFundTarget] = useState('');
  const [newFundDueDay, setNewFundDueDay] = useState('');
  const [newFundApy, setNewFundApy] = useState('');
  const [newFundColor, setNewFundColor] = useState('#3b82f6');
  const [newFundMerchants, setNewFundMerchants] = useState('');

  const [editingFundId, setEditingFundId] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [confirmDeleteFundId, setConfirmDeleteFundId] = useState(null);

  const [isTransferring, setIsTransferring] = useState(false);
  const [transferFrom, setTransferFrom] = useState('');
  const [transferTo, setTransferTo] = useState('');
  const [transferAmount, setTransferAmount] = useState('');

  useEffect(() => {
    fetchFunds();
  }, []);

  const fetchFunds = async () => {
    try {
      const [fundsRes, incomesRes] = await Promise.all([
        api.get('/funds'),
        api.get('/incomes')
      ]);
      setFunds(fundsRes.data);
      setIncomes(incomesRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAIScan = async () => {
    setIsScanning(true);
    setShowSuggestions(true);
    try {
      const res = await api.get('/plaid/analyze-history');
      setSuggestions(res.data.suggestions);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || 'Failed to analyze history. Is your bank linked?');
      setShowSuggestions(false);
    } finally {
      setIsScanning(false);
    }
  };

  const handleCreateSuggestion = async (suggestion) => {
    try {
      const res = await api.post('/funds', {
        name: suggestion.merchant,
        type: 'expense',
        target_amount: suggestion.averageAmount,
        due_day: suggestion.estimatedDueDay,
        color: '#8b5cf6',
        merchants: [suggestion.merchant]
      });
      setFunds([...funds, res.data]);
      setSuggestions(suggestions.filter(s => s.merchant !== suggestion.merchant));
      toast.success(`Bucket for ${suggestion.merchant} created! Don't forget to run Global Smart Allocate.`);
    } catch (err) {
      console.error(err);
      toast.error('Error creating bucket');
    }
  };

  const totalBalance = funds.reduce((sum, f) => sum + parseFloat(f.current_balance || 0), 0);
  const totalTarget = funds.reduce((sum, f) => sum + parseFloat(f.target_amount || 0), 0);
  const totalExpectedIncome = incomes.reduce((sum, inc) => {
    const freq = (inc.frequency || 'bi-weekly').toLowerCase();
    let mult = 26 / 12;
    if (freq === 'weekly') mult = 52 / 12;
    if (freq === 'semi-monthly') mult = 2;
    if (freq === 'monthly') mult = 1;
    return sum + (parseFloat(inc.amount || 0) * mult);
  }, 0);

  const handleTransfer = async (e) => {
    e.preventDefault();
    try {
      await api.post('/funds/transfer', {
        fromFundId: transferFrom,
        toFundId: transferTo,
        amount: transferAmount
      });
      fetchFunds();
      setIsTransferring(false);
      setTransferFrom('');
      setTransferTo('');
      setTransferAmount('');
      window.dispatchEvent(new Event('thread_notifications_updated'));
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || 'Transfer failed');
    }
  };

  const handleCreateFund = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post('/funds', {
        name: newFundName,
        type: newFundType,
        target_amount: parseFloat(newFundTarget) || 0,
        due_day: parseInt(newFundDueDay) || null,
        apy: parseFloat(newFundApy) || 0,
        color: newFundColor,
        merchants: newFundMerchants.split(',').map(m => m.trim()).filter(m => m)
      });
      setIsCreating(false);
      setNewFundName('');
      setNewFundTarget('');
      setNewFundDueDay('');
      setNewFundApy('');
      setNewFundMerchants('');
      fetchFunds();
      toast.success('Fund created successfully! ⚡ IMPORTANT: Please head over to the Allocations tab and click "Global Smart Allocate" to apply this new fund to your paycheck routing engine.');
    } catch (err) {
      console.error(err);
      toast.error('Error creating fund');
    }
  };

  const handleDelete = async (id) => {
    if (confirmDeleteFundId !== id) {
      setConfirmDeleteFundId(id);
      return;
    }
    try {
      await api.delete(`/funds/${id}`);
      setFunds(funds.filter(f => f.id !== id));
      setConfirmDeleteFundId(null);
    } catch (err) {
      console.error(err);
      toast.error('Error deleting fund');
    }
  };

  const startEditing = (fund) => {
    setEditingFundId(fund.id);
    setEditFormData({
      name: fund.name,
      type: fund.type,
      target_amount: fund.target_amount,
      current_balance: fund.current_balance,
      due_day: fund.due_day || '',
      apy: fund.apy || 0,
      color: fund.color,
      merchants: fund.merchants ? fund.merchants.map(m => m.match_pattern).join(', ') : ''
    });
  };

  const handleUpdateFund = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...editFormData, merchants: editFormData.merchants.split(',').map(m => m.trim()).filter(m => m) };
      const res = await api.put(`/funds/${editingFundId}`, payload);
      setEditingFundId(null);
      fetchFunds();
      toast.success('Fund updated! ⚡ IMPORTANT: If you changed the Target Amount or Due Day, please head to the Allocations tab and click "Global Smart Allocate" to recalculate your paycheck routing.');
    } catch (err) {
      console.error(err);
      toast.error('Error updating fund');
    }
  };

  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}><Loader2 className="text-muted" style={{ animation: 'spin 2s linear infinite' }} /></div>;
  }

  return (
    <div className="animate-fade-in" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
        <div>
          <h2 style={{ margin: 0 }}>Buckets</h2>
          <span className="text-muted">Manage your virtual buckets. Watch them fill up as paychecks arrive!</span>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button className="btn btn-outline" style={{ borderColor: 'var(--primary)', color: 'var(--primary)' }} onClick={handleAIScan} disabled={isScanning}>
            {isScanning ? <Loader2 size={18} style={{ animation: 'spin 2s linear infinite' }} /> : <Sparkles size={18} />}
            AI Scan
          </button>
          <button className="btn btn-outline" onClick={() => setIsTransferring(true)}>
            <ArrowRight size={18} /> Transfer
          </button>
          <button className="btn btn-primary" onClick={() => setIsCreating(true)}>
            <Plus size={18} /> Create Bucket
          </button>
        </div>
      </div>

      <div className="glass" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', padding: '1.5rem', marginBottom: '1rem' }}>
        <div>
          <span className="text-muted" style={{ fontSize: '0.85rem' }}>Total in Buckets</span>
          <h3 style={{ margin: '0.25rem 0 0 0', color: 'var(--primary)', fontSize: '1.5rem' }}>${totalBalance.toLocaleString(undefined, {minimumFractionDigits: 2})}</h3>
        </div>
        <div>
          <span className="text-muted" style={{ fontSize: '0.85rem' }}>Total Target Goals</span>
          <h3 style={{ margin: '0.25rem 0 0 0', fontSize: '1.5rem' }}>${totalTarget.toLocaleString(undefined, {minimumFractionDigits: 2})}</h3>
        </div>
        <div style={{ borderLeft: '1px solid var(--surface-border)', paddingLeft: '1rem' }}>
          <span className="text-muted" style={{ fontSize: '0.85rem' }}>Expected Monthly Income</span>
          <h3 style={{ margin: '0.25rem 0 0 0', color: 'var(--success)', fontSize: '1.5rem' }}>${totalExpectedIncome.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</h3>
        </div>
        <div style={{ borderLeft: '1px solid var(--surface-border)', paddingLeft: '1rem' }}>
          <span className="text-muted" style={{ fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>Leftover in Bank <span title="Expected Monthly Income minus Total Target Goals" style={{ cursor: 'help' }}>ⓘ</span></span>
          <h3 style={{ margin: '0.25rem 0 0 0', color: (totalExpectedIncome - totalTarget) < 0 ? 'var(--danger)' : 'var(--text)', fontSize: '1.5rem' }}>${(totalExpectedIncome - totalTarget).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</h3>
        </div>
      </div>

      {showSuggestions && (
        <div className="glass animate-fade-in" style={{ padding: 'var(--spacing-lg)', marginBottom: 'var(--spacing-md)', border: '1px solid var(--primary)', background: 'linear-gradient(145deg, rgba(59, 130, 246, 0.1), rgba(0,0,0,0.2))' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
            <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
               <Sparkles className="text-primary" size={20} /> AI Discovered Bills
            </h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <select 
                className="input-base" 
                value={suggestionSort} 
                onChange={(e) => setSuggestionSort(e.target.value)}
                style={{ padding: '0.4rem', fontSize: '0.9rem', width: 'auto' }}
              >
                <option value="amount_desc">Amount (Highest)</option>
                <option value="amount_asc">Amount (Lowest)</option>
                <option value="name_asc">Name (A-Z)</option>
                <option value="name_desc">Name (Z-A)</option>
                <option value="due_date_asc">Due Date (Earliest)</option>
                <option value="due_date_desc">Due Date (Latest)</option>
              </select>
              <button className="btn btn-glass" onClick={() => setShowSuggestions(false)}>Close</button>
            </div>
          </div>
          
          {isScanning ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
              <Loader2 size={32} style={{ animation: 'spin 2s linear infinite', marginBottom: '1rem', color: 'var(--primary)' }} />
              <p>Analyzing last 90 days of transactions...</p>
            </div>
          ) : suggestions.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
               No new recurring bills found. You're all caught up!
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
              {[...suggestions].sort((a, b) => {
                if (suggestionSort === 'amount_desc') return b.averageAmount - a.averageAmount;
                if (suggestionSort === 'amount_asc') return a.averageAmount - b.averageAmount;
                if (suggestionSort === 'name_asc') return a.merchant.localeCompare(b.merchant);
                if (suggestionSort === 'name_desc') return b.merchant.localeCompare(a.merchant);
                if (suggestionSort === 'due_date_asc') return (a.estimatedDueDay || 1) - (b.estimatedDueDay || 1);
                if (suggestionSort === 'due_date_desc') return (b.estimatedDueDay || 1) - (a.estimatedDueDay || 1);
                return 0;
              }).map((s, idx) => (
                <div key={idx} style={{ background: 'rgba(255,255,255,0.05)', padding: '1rem', borderRadius: '8px', border: '1px solid var(--surface-border)' }}>
                   <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                     <strong style={{ fontSize: '1.1rem' }}>{s.merchant}</strong>
                     <span style={{ color: 'var(--primary)', fontWeight: 'bold' }}>~${s.averageAmount.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                   </div>
                   <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
                     Spotted {s.occurrences} times in last 90 days. <br/>
                     Est. Due: {s.estimatedDueDay}{s.estimatedDueDay === 1 || s.estimatedDueDay === 21 || s.estimatedDueDay === 31 ? 'st' : s.estimatedDueDay === 2 || s.estimatedDueDay === 22 ? 'nd' : s.estimatedDueDay === 3 || s.estimatedDueDay === 23 ? 'rd' : 'th'}
                   </div>
                   <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }} onClick={() => handleCreateSuggestion(s)}>
                     + Create Auto-Pay Bucket
                   </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {isTransferring && (
        <div className="glass animate-fade-in" style={{ padding: 'var(--spacing-lg)', marginBottom: 'var(--spacing-md)', border: '1px solid var(--primary)' }}>
          <h3 style={{ marginTop: 0 }}>Transfer Funds</h3>
          <form onSubmit={handleTransfer} style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: '150px' }}>
              <label style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>From Bucket</label>
              <select value={transferFrom} onChange={(e) => setTransferFrom(e.target.value)} required>
                <option value="">Select source...</option>
                {funds.filter(f => parseFloat(f.current_balance) > 0).map(f => (
                  <option key={f.id} value={f.id}>{f.name} (${parseFloat(f.current_balance).toLocaleString(undefined, {minimumFractionDigits: 2})})</option>
                ))}
              </select>
            </div>
            <div style={{ flex: 1, minWidth: '150px' }}>
              <label style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>To Bucket</label>
              <select value={transferTo} onChange={(e) => setTransferTo(e.target.value)} required>
                <option value="">Select destination...</option>
                {funds.map(f => (
                  <option key={f.id} value={f.id} disabled={f.id === transferFrom}>{f.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>Amount ($)</label>
              <input type="number" step="any" min="0.01" value={transferAmount} onChange={(e) => setTransferAmount(e.target.value)} required />
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button type="button" className="btn btn-glass" onClick={() => setIsTransferring(false)}>Cancel</button>
              <button type="submit" className="btn btn-primary">Execute Transfer</button>
            </div>
          </form>
        </div>
      )}

      {isCreating && (
        <div className="glass animate-fade-in" style={{ padding: 'var(--spacing-lg)', marginBottom: 'var(--spacing-md)' }}>
          <h3 style={{ marginTop: 0 }}>Create New Bucket</h3>
          <form onSubmit={handleCreateFund} style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <label style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>Bucket Name</label>
              <input type="text" value={newFundName} onChange={(e) => setNewFundName(e.target.value)} required placeholder="e.g. Rent" />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>Type</label>
              <select value={newFundType} onChange={(e) => setNewFundType(e.target.value)}>
                <option value="expense">Fixed Expense</option>
                <option value="savings">Savings Goal</option>
                <option value="discretionary">Spending Money</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>Target Goal ($)</label>
              <input type="number" step="any" value={newFundTarget} onChange={(e) => setNewFundTarget(e.target.value)} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>Due Day (1-31)</label>
              <input type="number" min="1" max="31" value={newFundDueDay} onChange={(e) => setNewFundDueDay(e.target.value)} placeholder="e.g. 15" />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>APY %</label>
              <input type="number" step="any" min="0" max="1000" value={newFundApy} onChange={(e) => setNewFundApy(e.target.value)} placeholder="e.g. 5.0" />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>Color</label>
              <input type="color" value={newFundColor} onChange={(e) => setNewFundColor(e.target.value)} style={{ padding: '0', height: '42px', width: '60px' }} />
            </div>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <label style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>Merchant Names (comma-separated)</label>
              <input type="text" value={newFundMerchants} onChange={(e) => setNewFundMerchants(e.target.value)} placeholder="e.g. geico, netflix" />
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button type="button" className="btn btn-glass" onClick={() => setIsCreating(false)}>Cancel</button>
              <button type="submit" className="btn btn-primary">Create</button>
            </div>
          </form>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: 'var(--spacing-md)' }}>
        {funds.map((fund) => {
          const currentBalance = parseFloat(fund.current_balance || 0);
          const targetAmount = parseFloat(fund.target_amount || 0);
          const progressPercent = targetAmount > 0 ? Math.min(100, (currentBalance / targetAmount) * 100) : 0;
          const isFunded = targetAmount > 0 && currentBalance >= targetAmount;

          return (
            <div key={fund.id} className="glass" style={{ padding: 'var(--spacing-lg)', display: 'flex', flexDirection: 'column', gap: '1rem', position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: 0, left: 0, width: '4px', height: '100%', backgroundColor: fund.color }}></div>
              
              {editingFundId === fund.id ? (
                <form onSubmit={handleUpdateFund} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', zIndex: 1 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)' }}>Bucket Name</label>
                    <input type="text" value={editFormData.name} onChange={e => setEditFormData({...editFormData, name: e.target.value})} required style={{ padding: '0.4rem', fontSize: '0.9rem' }} />
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)' }}>Target ($)</label>
                      <input type="number" step="any" value={editFormData.target_amount} onChange={e => setEditFormData({...editFormData, target_amount: parseFloat(e.target.value) || 0})} style={{ padding: '0.4rem', fontSize: '0.9rem' }} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)' }}>Balance ($)</label>
                      <input type="number" step="any" value={editFormData.current_balance} onChange={e => setEditFormData({...editFormData, current_balance: parseFloat(e.target.value) || 0})} style={{ padding: '0.4rem', fontSize: '0.9rem' }} />
                    </div>
                    <div style={{ width: '80px' }}>
                      <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)' }}>Due Day</label>
                      <input type="number" min="1" max="31" value={editFormData.due_day} onChange={e => setEditFormData({...editFormData, due_day: parseInt(e.target.value) || ''})} style={{ padding: '0.4rem', fontSize: '0.9rem' }} />
                    </div>
                    <div style={{ width: '80px' }}>
                      <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)' }}>APY %</label>
                      <input type="number" step="any" min="0" max="1000" value={editFormData.apy} onChange={e => setEditFormData({...editFormData, apy: parseFloat(e.target.value) || 0})} style={{ padding: '0.4rem', fontSize: '0.9rem' }} />
                    </div>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)' }}>Merchant Names (comma-separated)</label>
                    <input type="text" value={editFormData.merchants} onChange={e => setEditFormData({...editFormData, merchants: e.target.value})} style={{ padding: '0.4rem', fontSize: '0.9rem' }} placeholder="e.g. geico, chase auto" />
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)' }}>Color</label>
                      <input type="color" value={editFormData.color} onChange={e => setEditFormData({...editFormData, color: e.target.value})} style={{ padding: '0', height: '34px', width: '50px' }} />
                    </div>
                    <div style={{ flex: 1, display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                      <button type="button" className="btn btn-glass" style={{ padding: '0.4rem 0.8rem' }} onClick={() => setEditingFundId(null)}>Cancel</button>
                      <button type="submit" className="btn btn-primary" style={{ padding: '0.4rem 0.8rem' }}>Save</button>
                    </div>
                  </div>
                </form>
              ) : (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <h3 style={{ margin: '0 0 0.25rem 0' }}>{fund.name}</h3>
                      <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)', textTransform: 'capitalize' }}>
                        {fund.type} {fund.due_day ? `• Due on the ${fund.due_day}${parseInt(fund.due_day) === 1 || parseInt(fund.due_day) === 21 || parseInt(fund.due_day) === 31 ? 'st' : parseInt(fund.due_day) === 2 || parseInt(fund.due_day) === 22 ? 'nd' : parseInt(fund.due_day) === 3 || parseInt(fund.due_day) === 23 ? 'rd' : 'th'}` : ''}
                        {fund.apy > 0 && <span style={{ color: 'var(--success)', marginLeft: '0.5rem', fontWeight: 600 }}>• {parseFloat(fund.apy).toFixed(2)}% APY</span>}
                      </span>
                      {fund.merchants && fund.merchants.length > 0 && (
                         <div style={{ display: 'flex', gap: '0.25rem', marginTop: '0.4rem', flexWrap: 'wrap' }}>
                            {fund.merchants.map(m => (
                               <span key={m.id} style={{ fontSize: '0.75rem', padding: '0.2rem 0.5rem', background: 'rgba(255,255,255,0.1)', borderRadius: '4px' }}>{m.match_pattern}</span>
                            ))}
                         </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button className="btn btn-glass" style={{ padding: '0.4rem', color: 'var(--text-muted)' }} onClick={() => startEditing(fund)}>
                        <Edit2 size={16} />
                      </button>
                      {confirmDeleteFundId === fund.id ? (
                        <button className="btn btn-primary" style={{ padding: '0.4rem 0.8rem', backgroundColor: 'var(--danger)', color: '#fff', border: 'none' }} onClick={() => handleDelete(fund.id)}>
                          Confirm
                        </button>
                      ) : (
                        <button className="btn btn-glass" style={{ padding: '0.4rem', color: 'var(--text-muted)' }} onClick={() => handleDelete(fund.id)}>
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                  
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '0.5rem' }}>
                      <h2 style={{ margin: 0, color: isFunded ? 'var(--success)' : 'var(--text)' }}>${currentBalance.toLocaleString(undefined, {minimumFractionDigits: 2})}</h2>
                      {targetAmount > 0 && (
                        <span className="text-muted" style={{ fontSize: '0.9rem' }}>of ${targetAmount.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                      )}
                    </div>

                    {targetAmount > 0 && (
                      <div style={{ width: '100%', height: '8px', backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: '4px', overflow: 'hidden' }}>
                        <div style={{ 
                          height: '100%', 
                          backgroundColor: isFunded ? 'var(--success)' : fund.color,
                          width: `${progressPercent}%`,
                          transition: 'width 1s ease-in-out, background-color 0.3s ease'
                        }}></div>
                      </div>
                    )}
                    
                    {isFunded && (
                      <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: 'var(--success)', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                        <ArrowRight size={14} /> Fully Funded
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          );
        })}

        {funds.length === 0 && !isCreating && (
          <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
            No buckets created yet. Click "Create Bucket" to get started.
          </div>
        )}
      </div>
    </div>
  );
}
